import React, { useEffect, useMemo, useState } from "react";
import { api, fmtPLN, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { KPIStatCard } from "@/components/KPIStatCard";
import { AlertItem } from "@/components/AlertItem";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, Bell, ClipboardCheck, Ticket, Truck, ShieldAlert, ChevronDown, Info, Ticket as TicketIcon } from "lucide-react";
import { toast } from "sonner";
import { Navigate, useNavigate } from "react-router-dom";

export default function OwnerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenueOpen, setRevenueOpen] = useState(false);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/owner");
      setData(data);
    } catch (e) {
      toast.error("Nie udało się wczytać danych");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  if (user?.role === "manager") return <Navigate to="/dashboard" replace />;
  if (loading) return <DashboardSkeleton />;
  if (!data) return null;

  const k = data.kpis;

  async function ackAlert(id) {
    try {
      await api.post(`/alerts/${id}/acknowledge`);
      toast.success("Alert potwierdzony");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  }

  async function closeAlert(alert) {
    const reason = window.prompt("Powód zamknięcia alertu (opcjonalnie):") || "";
    try {
      await api.post(`/alerts/${alert.id}/close`, { reason: reason.trim() || null });
      toast.success("Alert zamknięty");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  }

  return (
    <div className="space-y-6" data-testid="owner-dashboard">
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tt-display">Dashboard Właściciela</h1>
        <p className="text-[var(--tt-muted)] mt-1">Czy wszystko OK? Czy ktoś musi coś zatwierdzić? Czy gdzieś zginęły pieniądze?</p>
      </div>

      {/* HERO KPI — 3 największe pytania właściciela */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="owner-hero-kpi">
        <RevenueHeroTile
          breakdown={data.revenue_today_breakdown}
          value={k.revenue_today}
          open={revenueOpen}
          onToggle={() => setRevenueOpen((v) => !v)}
          testId="hero-revenue-today"
        />
        <HeroTile
          label="Do zatwierdzenia"
          value={k.pending_reconciliations}
          suffix={k.pending_reconciliations === 1 ? "rozliczenie" : "rozliczeń"}
          icon={ClipboardCheck}
          tone={k.pending_reconciliations > 0 ? "warning" : "ok"}
          onClick={() => navigate("/daily?status=submitted")}
          testId="hero-pending-recon"
        />
        <HeroTile
          label="Krytyczne alerty"
          value={k.critical_alerts_open}
          suffix={k.critical_alerts_open === 1 ? "alert" : "alertów"}
          icon={k.critical_alerts_open ? Bell : ShieldAlert}
          tone={k.critical_alerts_open > 0 ? "critical" : "ok"}
          sublabel={k.critical_alerts_open ? "Sprawdź teraz" : "Wszystko OK"}
          onClick={() => navigate("/alerts?level=critical&status=open")}
          testId="hero-critical-alerts"
        />
      </div>

      {/* Revenue breakdown — rozwijany accordion z podziałem per dział */}
      {revenueOpen && data.revenue_today_breakdown && (
        <RevenueBreakdownPanel breakdown={data.revenue_today_breakdown} onClose={() => setRevenueOpen(false)} />
      )}

      {/* Secondary KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="owner-kpi-row">
        <KPIStatCard label="Obrót 7d" value={k.revenue_7d} currency icon={TrendingUp} testId="kpi-revenue-7d" to="/daily" />
        <KPIStatCard label="Obrót 30d" value={k.revenue_30d} currency icon={TrendingUp} testId="kpi-revenue-30d" to="/daily" />
        <KPIStatCard label="Aktywne vouchery" sublabel={`${k.vouchers_active_count} szt.`} value={k.vouchers_active_value} currency icon={Ticket} accent="accent" testId="kpi-vouchers-active" to="/vouchers" />
        <KPIStatCard label="Zamówienia do akcept." value={k.orders_pending} icon={Truck} testId="kpi-orders-pending" to="/orders?status=submitted" />
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Critical alerts — full width now that variances panel is removed */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="tt-section-title">Pilne i krytyczne alerty</h2>
            <button onClick={() => navigate("/alerts")} className="text-sm font-medium text-[var(--tt-primary)] hover:underline" data-testid="see-all-alerts">Zobacz wszystkie →</button>
          </div>
          {data.critical_alerts.length === 0 ? (
            <Card className="tt-card"><CardContent className="p-6 text-center text-[var(--tt-muted)]">Brak pilnych alertów — dobrze.</CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="owner-alerts-panel">
              {data.critical_alerts.map((a) => (
                <AlertItem key={a.id} alert={{ ...a, status: "open" }} onAck={ackAlert} onClose={closeAlert} currentUserRole={user.role} currentUserId={user.id} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HeroTile({ label, value, currency, suffix, sublabel, icon: Icon, tone = "primary", onClick, testId }) {
  const toneClasses = {
    primary: "bg-[var(--tt-surface)] border-[var(--tt-border)]",
    ok: "bg-[var(--tt-success-bg)] border-[var(--tt-success-border)]",
    warning: "bg-[var(--tt-warn-bg)] border-[var(--tt-warn-border)]",
    critical: "bg-[var(--tt-critical-bg)] border-[var(--tt-critical-border)]",
    muted: "bg-[var(--tt-surface-2)] border-[var(--tt-border)]",
  }[tone] || "";
  const fg = {
    primary: "text-[var(--tt-text)]",
    ok: "text-[var(--tt-success-fg)]",
    warning: "text-[var(--tt-warn-fg)]",
    critical: "text-[var(--tt-critical-fg)]",
    muted: "text-[var(--tt-muted)]",
  }[tone] || "";
  let display;
  if (currency) {
    display = fmtPLN(value || 0);
  } else if (typeof value === "number") {
    display = value.toLocaleString("pl-PL");
  } else {
    display = value ?? "—";
  }
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-2xl border ${toneClasses} p-5 transition-transform hover:scale-[1.01] active:scale-[0.99] tt-focusable min-h-[140px] flex flex-col justify-between`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-widest font-semibold ${fg} opacity-80`}>{label}</div>
        {Icon ? <Icon className={`h-5 w-5 ${fg} opacity-70`} /> : null}
      </div>
      <div>
        <div className={`tt-mono font-bold leading-none ${fg} text-4xl md:text-5xl`}>
          {display}
          {suffix && <span className="text-base font-normal opacity-70 ml-2 align-middle">{suffix}</span>}
        </div>
        {sublabel && <div className={`mt-2 text-sm ${fg} opacity-75`}>{sublabel}</div>}
      </div>
    </button>
  );
}

// Labels dla działów
const DEPT_LABELS = {
  BAR: "BAR",
  SALA: "SALA",
  MASAZ: "MASAŻ",
  KIEROWCY: "KIEROWCY / DOWOZY",
  KUCHNIA: "KUCHNIA",
};

function RevenueHeroTile({ breakdown, value, open, onToggle, testId }) {
  const total = Number(value || 0);
  const tone = total > 0 ? "primary" : "muted";
  const toneClasses = {
    primary: "bg-[var(--tt-surface)] border-[var(--tt-border)]",
    muted: "bg-[var(--tt-surface-2)] border-[var(--tt-border)]",
  }[tone];
  const fg = {
    primary: "text-[var(--tt-text)]",
    muted: "text-[var(--tt-muted)]",
  }[tone];
  const deptCount = breakdown?.departments_with_data?.length || 0;
  const sublabel = deptCount > 0
    ? `${deptCount} ${deptCount === 1 ? "dział" : "działy"} • kliknij, aby zobaczyć rozbicie`
    : "Brak rozliczeń dziś • kliknij, aby zobaczyć szczegóły";
  return (
    <button
      onClick={onToggle}
      className={`text-left rounded-2xl border ${toneClasses} p-5 transition-transform hover:scale-[1.01] active:scale-[0.99] tt-focusable min-h-[140px] flex flex-col justify-between relative`}
      data-testid={testId}
      aria-expanded={open}
    >
      <div className="flex items-center justify-between">
        <div className={`tt-mono text-xs uppercase tracking-widest ${fg} opacity-80`}>
          Dzisiejszy obrót brutto
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className={`h-5 w-5 ${fg} opacity-70`} />
          <ChevronDown className={`h-4 w-4 ${fg} opacity-60 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>
      <div>
        <div className={`tt-brand leading-none ${fg} text-4xl md:text-5xl`}>
          {fmtPLN(total)}
        </div>
        <div className={`mt-2 text-xs ${fg} opacity-75`}>{sublabel}</div>
      </div>
    </button>
  );
}

function RevenueBreakdownPanel({ breakdown, onClose }) {
  // P3 (2026-07-10): useMemo dla ciężkich obliczeń — zapobiega re-computingowi
  // sortowania/filtra/reduce na każdym renderze parent'a.
  const { entries, total, vred, vredEntries, vredTotal, maxVal, sortedEntries } = useMemo(() => {
    const _entries = Object.entries(breakdown.by_department || {});
    const _vred = breakdown.voucher_redeemed_today || {};
    const _vredEntries = Object.entries(_vred.by_department || {}).filter(([, v]) => Number(v) > 0);
    const _maxVal = _entries.reduce((m, [, v]) => Math.max(m, Number(v) || 0), 0) || 1;
    const _sorted = [..._entries].sort((a, b) => Number(b[1]) - Number(a[1]));
    return {
      entries: _entries,
      total: Number(breakdown.total || 0),
      vred: _vred,
      vredEntries: _vredEntries,
      vredTotal: Number(_vred.total || 0),
      maxVal: _maxVal,
      sortedEntries: _sorted,
    };
  }, [breakdown]);

  return (
    <Card className="tt-card" data-testid="revenue-breakdown-panel">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold tt-display">Dzisiejszy obrót — rozbicie per dział</div>
            <div className="text-xs text-[var(--tt-muted)] mt-1">{breakdown.scope}</div>
          </div>
          <button
            onClick={onClose}
            className="text-xs text-[var(--tt-muted)] hover:text-[var(--tt-text)] underline"
            data-testid="revenue-breakdown-close"
          >
            Zwiń
          </button>
        </div>

        {/* Per-department bars */}
        {entries.length === 0 ? (
          <div className="text-sm text-[var(--tt-muted)] py-4 text-center">
            Żaden dział nie ma jeszcze rozliczeń w statusie <strong>Wysłane</strong>, <strong>Zatwierdzone</strong> ani <strong>Skorygowane</strong>.
          </div>
        ) : (
          <div className="space-y-2" data-testid="revenue-breakdown-bars">
            {sortedEntries
              .map(([dept, val]) => {
                const v = Number(val || 0);
                const pct = total > 0 ? (v / total) * 100 : 0;
                const widthPct = maxVal > 0 ? (v / maxVal) * 100 : 0;
                return (
                  <div key={dept} className="flex items-center gap-3" data-testid={`revenue-row-${dept}`}>
                    <div className="w-32 text-xs font-semibold tracking-wide">{DEPT_LABELS[dept] || dept}</div>
                    <div className="flex-1 h-6 bg-[var(--tt-surface-2)] rounded relative overflow-hidden">
                      <div
                        className="h-full bg-[var(--tt-primary)] opacity-80 rounded transition-all"
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                    <div className="w-32 text-right tt-mono text-sm font-semibold">{fmtPLN(v)}</div>
                    <div className="w-12 text-right text-xs text-[var(--tt-muted)]">{pct.toFixed(0)}%</div>
                  </div>
                );
              })}
            <div className="flex items-center gap-3 pt-2 border-t border-[var(--tt-border)]">
              <div className="w-32 text-xs font-bold uppercase">SUMA</div>
              <div className="flex-1" />
              <div className="w-32 text-right tt-mono text-base font-bold">{fmtPLN(total)}</div>
              <div className="w-12 text-right text-xs text-[var(--tt-muted)]">100%</div>
            </div>
          </div>
        )}

        {/* Voucher redemptions — informational only */}
        <div className="rounded-lg border border-[var(--tt-border)] bg-[var(--tt-surface-2)] p-3 space-y-2" data-testid="voucher-redeemed-info">
          <div className="flex items-center gap-2">
            <TicketIcon className="h-4 w-4 text-[var(--tt-accent)]" />
            <div className="text-xs font-semibold">
              Zrealizowane vouchery dziś: <span className="tt-mono">{fmtPLN(vredTotal)}</span>
            </div>
          </div>
          {vredEntries.length > 0 && (
            <div className="text-xs text-[var(--tt-muted)] pl-6">
              {vredEntries.map(([d, v]) => `${DEPT_LABELS[d] || d}: ${fmtPLN(Number(v))}`).join(" • ")}
            </div>
          )}
          <div className="text-xs text-[var(--tt-muted)] pl-6 leading-relaxed">
            <Info className="inline h-3 w-3 mr-1 -mt-0.5" />
            {vred.note || "Realizacje voucherów nie są wliczone do dzisiejszego obrotu — przychód został zaksięgowany w dniu sprzedaży vouchera."}
          </div>
        </div>

        {/* Rules box */}
        <div className="rounded-lg border border-[var(--tt-border)] p-3 text-xs text-[var(--tt-muted)] space-y-1">
          <div className="font-semibold text-[var(--tt-text)]">Co zawiera ten obrót:</div>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>Brutto z VAT — pole <code>total_revenue</code> z POS</li>
            <li>Wszystkie metody: gotówka, karta, online/przelewy, vouchery zawarte w POS</li>
            <li>Liczone statusy: <strong>{breakdown.counted_statuses?.join(", ")}</strong></li>
          </ul>
          <div className="font-semibold text-[var(--tt-text)] mt-2">Co NIE jest wliczone:</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {(breakdown.excludes || []).map((e, i) => <li key={i}>{e}</li>)}
            <li>Zrealizowane vouchery (pokazane osobno wyżej)</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}



function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-80" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}
